// packages/main/app/web/components-v2/UnifiedImageDialog/styles.ts
import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../Theme/theme';

const DESTINATION_BANNER_HEIGHT = 40;
const CLOSE_BUTTON_SIZE = 24;
const CLOSE_ICON_SIZE = 12;

export const s = StyleSheet.create({
	tabTriggerFreeImages: {
		width: '100%',
		whiteSpace: 'nowrap',
	},
	dialogContent: {
		maxWidth: '90vw',
		maxHeight: '90vh',
		width: '1200px',
		height: '700px',
		display: 'flex',
		flexDirection: 'column',
		padding: 0,
	},
	dialogHeader: {
		padding: `${THEME.spacing.lg}px ${THEME.spacing.lg}px ${THEME.spacing.md}px`,
	},
	dialogBody: {
		display: 'flex',
		flex: 1,
		overflow: 'hidden',
		padding: 0,
	},
	tabsContainer: {
		display: 'flex',
		flex: 1,
		overflow: 'hidden',
	},
	// The shared primary tab list supplies the rule and the active tab's stroke on its right edge, as in
	// the admin nav. `!important` beats its `fit-content` height, which would end the rule short of the
	// dialog.
	verticalTabList: {
		flexShrink: 0,
		height: 'auto !important',
		maxWidth: '20%',
		overflowY: 'auto',
		paddingBottom: SPACE[16],
		paddingTop: SPACE[16],
		width: '180px',
	},
	tabTrigger: {
		alignItems: 'center',
		display: 'flex',
		// Labels wrap in the narrow list, and a button centres wrapped text.
		textAlign: 'left',
		':is([data-state="active"])': {
			backgroundColor: THEME.colors.background.main,
		},
	},
	tabContent: {
		flex: 1,
		minHeight: 0,
		overflow: 'auto',
		padding: `${THEME.spacing.lg}px`,
	},
	tabIcon: {
		width: '20px',
		height: '20px',
	},
	// Deliberately no borderTop: the AI tabs already draw one above their chat input, and a second rule
	// immediately below it reads as a stray double line.
	saveToLibraryBar: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.lg}px`,
		flexShrink: 0,
	},
	saveToLibraryLabel: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		cursor: 'pointer',
		marginBottom: 0,
	},
	destinationBanner: {
		alignItems: 'center',
		background: THEME.colors.background.main,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		boxSizing: 'border-box',
		color: THEME.colors.text.light,
		display: 'flex',
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.sm,
		gap: SPACE[8],
		minHeight: DESTINATION_BANNER_HEIGHT,
		padding: `${SPACE[8]}px ${SPACE[16]}px ${SPACE[8]}px ${SPACE[24]}px`,
	},
	destinationBannerText: {
		flex: 1,
		minWidth: 0,
	},
	// Lines up with the destination banner's close, so the cross doesn't move as the banner comes and goes.
	closeBar: {
		display: 'flex',
		flexShrink: 0,
		justifyContent: 'flex-end',
		padding: `${SPACE[8]}px ${SPACE[16]}px 0`,
	},
	// A faint cross that firms up on hover, like the shared dialog close.
	closeButton: {
		alignItems: 'center',
		background: 'none',
		border: 'none',
		borderRadius: 4,
		color: THEME.colors.text.main,
		cursor: 'pointer',
		display: 'flex',
		flexShrink: 0,
		height: CLOSE_BUTTON_SIZE,
		justifyContent: 'center',
		opacity: 0.6,
		padding: 0,
		transition: 'opacity 150ms cubic-bezier(0.4, 0, 0.2, 1)',
		width: CLOSE_BUTTON_SIZE,
		':hover': {
			opacity: 1,
		},
		':focus-visible': {
			opacity: 1,
			outline: `2px solid ${THEME.colors.primary.main}`,
			outlineOffset: 2,
		},
	},
	closeIcon: {
		height: CLOSE_ICON_SIZE,
		width: CLOSE_ICON_SIZE,
	},
	destinationLogo: {
		flexShrink: 0,
		height: 16,
		width: 16,
	},

	logoOverlayTab: {
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[16],
		height: '100%',
		minHeight: 0,
	},
	logoOverlayWarning: {
		background: THEME.colors.warning.light,
		borderRadius: THEME.common.borderRadius,
		color: THEME.colors.text.dark,
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
		padding: `${SPACE[8]}px ${SPACE[16]}px`,
	},
	logoOverlayToggleRow: {
		alignItems: 'center',
		display: 'flex',
		flexShrink: 0,
		gap: SPACE[8],
	},
	logoOverlayToggleLabel: {
		color: THEME.colors.text.main,
		cursor: 'pointer',
		fontSize: THEME.typography.fontSizes.sm,
		marginBottom: 0,
	},
	logoOverlayControls: {
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		gap: SPACE[8],
		maxWidth: 420,
		width: '100%',
	},
	logoOverlayNote: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
		textAlign: 'center',
	},
	logoOverlayStage: {
		alignItems: 'center',
		alignSelf: 'stretch',
		display: 'flex',
		flex: '1 1 0',
		gap: SPACE[8],
		justifyContent: 'center',
		minHeight: 0,
		overflow: 'hidden',
	},
	logoOverlayPreview: {
		maxHeight: '100%',
	},
	logoOverlayCount: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
	},
	logoOverlayEmpty: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.sm,
		height: '100%',
		justifyContent: 'center',
		textAlign: 'center',
	},

	cropTab: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[8],
	},
	cropPager: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[8],
		justifyContent: 'center',
	},
	cropCount: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
	},
	cropLoading: {
		alignItems: 'center',
		display: 'flex',
		height: 400,
		justifyContent: 'center',
	},
	cropNote: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
		textAlign: 'center',
	},
	cropError: {
		color: THEME.colors.danger.dark,
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
		textAlign: 'center',
	},
	cropEmpty: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.sm,
		height: '100%',
		justifyContent: 'center',
		textAlign: 'center',
	},

	tabContentWrapper: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
	},
	uploadContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.lg,
		height: '100%',
	},
	dropzone: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		border: `2px dashed ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		padding: THEME.spacing.xl,
		cursor: 'pointer',
		transition: 'all 0.2s',
		':hover': {
			borderColor: THEME.colors.primary.main,
			backgroundColor: THEME.colors.primary.light,
		},
		'[data-drag-active]': {
			borderColor: THEME.colors.primary.main,
			backgroundColor: THEME.colors.hover.light,
		},
	},
	dropzoneContent: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: THEME.spacing.md,
		textAlign: 'center',
	},
	dropzoneIcon: {
		width: 64,
		height: 64,
		color: THEME.colors.icon.gray,
	},
	dropzoneTitle: {
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	dropzoneDescription: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	fileInput: {
		display: 'none',
	},
	errorMessage: {
		padding: THEME.spacing.sm,
		backgroundColor: THEME.colors.danger.light,
		color: THEME.colors.danger.dark,
		borderRadius: THEME.common.borderRadius,
		fontSize: THEME.typography.fontSizes.sm,
	},
	chatContainer: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		gap: THEME.spacing.md,
	},
	chatMessages: {
		flex: 1,
		overflow: 'auto',
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
	},
	chatInputContainer: {
		borderTop: `1px solid ${THEME.colors.border.light}`,
		paddingTop: THEME.spacing.md,
	},
	previewSection: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
	},
	previewImageContainer: {
		flex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: THEME.spacing.xl,
		backgroundColor: THEME.colors.background.light,
	},
	previewImage: {
		maxWidth: '100%',
		maxHeight: '100%',
		objectFit: 'contain',
		borderRadius: THEME.common.borderRadius,
	},
	previewActions: {
		display: 'flex',
		gap: THEME.spacing.md,
		padding: THEME.spacing.lg,
		borderTop: `1px solid ${THEME.colors.border.light}`,
		justifyContent: 'flex-end',
	},
	bulkRebrandingContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
	},
	imageContainer: {
		height: 'auto',
	},
	image: {
		width: 120,
		height: 120,
		borderRadius: THEME.common.borderRadius,
		objectFit: 'cover',
	},
	checkboxLabel: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	tooltipTrigger: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	providedImagesTab: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[16],
		height: '100%',
		overflowY: 'auto',
		padding: SPACE[24],
		boxSizing: 'border-box',
	},
	providedImagesTitle: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	providedImagesEmpty: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '100%',
		padding: SPACE[24],
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	providedImagesGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
		gap: SPACE[16],
	},
	providedImageCard: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		gap: SPACE[4],
		height: 'auto',
		padding: SPACE[8],
		textAlign: 'left',
		whiteSpace: 'normal',
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		':hover:not(:disabled)': {
			borderColor: THEME.colors.primary.main,
			backgroundColor: THEME.colors.hover.light,
		},
	},
	providedImageFrame: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: 140,
		marginBottom: SPACE[4],
		overflow: 'hidden',
		borderRadius: THEME.common.borderRadiusSm,
		backgroundColor: THEME.colors.background.main,
	},
	providedImageBadge: {
		position: 'absolute',
		top: SPACE[8],
		left: SPACE[8],
		padding: `${SPACE[4]}px ${SPACE[8]}px`,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.primary.main,
		color: THEME.colors.primary.contrastText,
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	providedImage: {
		maxWidth: '100%',
		maxHeight: '100%',
		objectFit: 'contain',
	},
	providedImageMissing: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	providedImageName: {
		...THEME.common.truncate,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	providedImageDetail: {
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
	},
});
